import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal, Button, Form, Row, Col, Alert, Spinner, Table, InputGroup, Pagination, Badge,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import transactionTikaneService from '../services/transactionTikaneService';
import tikaneService from '../services/tikaneService';
import clientService from '../services/clientService';
import ProtectedSection from './ProtectedSection';
import ReceiptPrintButton from './ReceiptPrintButton';
import { defaultDateRange, validateDateRange } from '../utils/dateFilters';

export default function TransactionTiKaneModal({ tiKaneId, show, onHide }) {
  const { register, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
    defaultValues: { type: 'CREDIT', amount: '' },
  });

  const initialDates = useMemo(() => defaultDateRange(), []);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editingTx, setEditingTx] = useState(null);
  const [ruleError, setRuleError] = useState('');
  const [account, setAccount] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientCin, setClientCin] = useState('');
  const [summary, setSummary] = useState({});
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const type = watch('type', 'CREDIT');
  const amount = Number(watch('amount') || 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const totalValue = Number(account?.totalValue || 0);
  const finalValue = Number(account?.finalValue || 0);
  const dailyValue = Number(account?.dailyValue || 0);
  const feePercent = account?.feePercent != null
    ? Number(account.feePercent)
    : (totalValue > 0 ? 100 * (1 - finalValue / totalValue) : 5);
  const feeFixed = Number(account?.feeFixedGourde || 0);
  const status = String(account?.status || 'OPEN').toUpperCase();
  const isClosed = ['CLOSED', 'PAYÉ', 'PAYE', 'EARLY_TERMINATED'].includes(status);

  const fetchData = useCallback(async (targetPage = page) => {
    if (!show || !tiKaneId) return;
    const dateError = validateDateRange(dateFrom, dateTo);
    if (dateError) { setRuleError(dateError); setTxs([]); setTotal(0); return; }
    setLoading(true); setRuleError('');
    try {
      const [acc, list] = await Promise.all([
        tikaneService.get(tiKaneId),
        transactionTikaneService.list(tiKaneId, {
          page: targetPage, pageSize, search: filter, type: typeFilter,
          dateFrom, dateTo, sortKey, sortDir,
        }),
      ]);
      setAccount(acc || null);
      setTxs(list?.data || []);
      setSummary(list?.summary || {});
      setTotal(Number(list?.total || 0));
      const pages = Math.max(1, Number(list?.totalPages || Math.ceil(Number(list?.total || 0) / pageSize)));
      if (targetPage > pages) setPage(pages);

      if (acc?.clientId) {
        try {
          const c = await clientService.get(acc.clientId);
          setClientName(`${c.firstName || ''} ${c.lastName || ''}`.trim());
          setClientCin(c.nifCin || '');
        } catch { setClientName(''); setClientCin(''); }
      } else if (acc?.Minor) {
        setClientName(`${acc.Minor.firstName || ''} ${acc.Minor.lastName || ''}`.trim());
        setClientCin(acc.Minor?.parent?.nifCin || '');
      } else { setClientName(''); setClientCin(''); }
    } catch (e) {
      setRuleError(e?.response?.data?.message || e?.message || 'Erreur lors du chargement du compte.');
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, filter, page, pageSize, show, sortDir, sortKey, tiKaneId, typeFilter]);

  useEffect(() => { if (show) fetchData(page); }, [show, page, fetchData]);
  useEffect(() => { if (show) setPage(1); }, [filter, typeFilter, dateFrom, dateTo, sortKey, sortDir, pageSize, show]);
  useEffect(() => {
    if (!show) {
      setTxs([]); setAccount(null); setEditingTx(null); setFilter(''); setTypeFilter('');
      setPage(1); setTotal(0); setSortKey('createdAt'); setSortDir('desc'); setRuleError('');
      const dates = defaultDateRange(); setDateFrom(dates.dateFrom); setDateTo(dates.dateTo);
      reset({ type: 'CREDIT', amount: '' });
    }
  }, [show, reset]);

  useEffect(() => {
    if (!show || editingTx) return;
    if (type === 'CREDIT') setValue('amount', dailyValue > 0 ? dailyValue.toFixed(2) : '');
    else setValue('amount', '');
  }, [dailyValue, editingTx, setValue, show, type]);

  const remainderToPay = Number(summary.remainderToPay || 0);
  const maxWithdrawAllowed = Number(summary.maxWithdrawAllowed || 0);
  const remainingToClient = Number(summary.remainingToClient || 0);
  const currentBalance = Number(summary.currentBalance || 0);
  const sumCredit = Number(summary.sumCredit || 0);
  const sumDebit = Number(summary.sumDebit || 0);
  const minReserve = Number(summary.minReserve || 0);

  const localRuleError = useMemo(() => {
    if (isClosed && !editingTx) return 'Ce compte est clôturé. Aucune nouvelle opération n’est possible.';
    if (!amount || amount <= 0) return '';
    if (type === 'CREDIT' && remainderToPay <= 0) return 'Le compte est totalement payé. Aucun dépôt supplémentaire n’est possible.';
    if (type === 'CREDIT' && amount > remainderToPay + 1e-6) return `Le dépôt dépasse le reste à payer (${remainderToPay.toFixed(2)}).`;
    if (type === 'DEBIT' && amount > maxWithdrawAllowed + 1e-6) return `Retrait trop élevé. Maximum autorisé : ${maxWithdrawAllowed.toFixed(2)}.`;
    return '';
  }, [amount, editingTx, isClosed, maxWithdrawAllowed, remainderToPay, type]);

  const paginationItems = useMemo(() => {
    const items = [
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />,
    ];
    const from = Math.max(1, page - 2); const to = Math.min(totalPages, page + 2);
    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p += 1) items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );
    return items;
  }, [page, totalPages]);

  const refreshAfterMutation = async () => { setPage(1); await fetchData(1); };
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette transaction ?')) return;
    try { await transactionTikaneService.remove(tiKaneId, id); await refreshAfterMutation(); }
    catch (e) { setRuleError(e?.response?.data?.message || e?.message || 'Suppression impossible.'); }
  };
  const handleEdit = (tx) => {
    setEditingTx(tx); reset({ type: tx.type, amount: Number(tx.amount).toFixed(2) }); setRuleError('');
  };
  const cancelEdit = () => {
    setEditingTx(null); reset({ type: 'CREDIT', amount: dailyValue > 0 ? dailyValue.toFixed(2) : '' });
  };

  const onSubmit = async (form) => {
    const numericAmount = Number(form.amount);
    if (!numericAmount || numericAmount <= 0) { setRuleError('Montant invalide.'); return; }
    if (localRuleError) { setRuleError(localRuleError); return; }
    try {
      setRuleError('');
      if (editingTx) await transactionTikaneService.update(tiKaneId, editingTx.id, { type: form.type, amount: numericAmount });
      else await transactionTikaneService.create(tiKaneId, { type: form.type, amount: numericAmount });
      setEditingTx(null); reset({ type: 'CREDIT', amount: '' }); await refreshAfterMutation();
    } catch (e) { setRuleError(e?.response?.data?.message || e?.message || 'Opération impossible.'); }
  };

  const handlePayout = async () => {
    if (!window.confirm('Effectuer le paiement final et clôturer le compte ?')) return;
    try {
      setRuleError('');
      const resp = await transactionTikaneService.payout(tiKaneId);
      if (resp?.message) window.alert(resp.message);
      await refreshAfterMutation();
    } catch (e) { setRuleError(e?.response?.data?.message || e?.message || 'Paiement final impossible.'); }
  };

  const buildReceiptDocument = (tx) => {
    const username = tx?.agent?.username || '—';
    return {
      title: `Reçu transaction Ti Kanè${tx?.codeTransaction ? ` - ${tx.codeTransaction}` : ''}`,
      rows: [
        { label: 'Compte', value: account?.accountNumber || `#${tiKaneId}` },
        { label: 'Client', value: clientName || '—' }, { label: 'CIN/NIF', value: clientCin || '—' },
        { label: 'ID transaction', value: tx?.id || '—' }, { label: 'Code transaction', value: tx?.codeTransaction || '—' },
        { label: 'Type', value: tx?.type === 'CREDIT' ? 'Dépôt' : 'Retrait' },
        { label: 'Montant', value: `${Number(tx?.amount || 0).toFixed(2)} HTG` },
        { label: 'Solde après', value: `${Number(tx?.balanceAfter || 0).toFixed(2)} HTG` },
        { label: 'Date', value: tx?.createdAt ? new Date(tx.createdAt).toLocaleString() : '—' },
        { label: 'Utilisateur', value: username },
      ],
      signatures: { client: 'Signature client', cashier: username },
    };
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton><Modal.Title>Transactions du compte {account?.accountNumber ? `#${account.accountNumber}` : `#${tiKaneId || ''}`}</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading && !account ? <div className="text-center py-4"><Spinner animation="border" /></div> : <>
          {(ruleError || localRuleError) && <Alert variant="danger">{ruleError || localRuleError}</Alert>}
          {account && <>
            <Row className="g-2 mb-3">
              <Col md={3}><Alert variant="secondary" className="mb-0"><strong>Client</strong><div>{clientName || '—'}</div><small>{clientCin || ''}</small></Alert></Col>
              <Col md={3}><Alert variant="info" className="mb-0"><strong>Solde actuel</strong><div>{currentBalance.toFixed(2)} HTG</div><small>Statut : {account.status || '—'}</small></Alert></Col>
              <Col md={3}><Alert variant="light" className="mb-0"><strong>Dépôts</strong><div>{sumCredit.toFixed(2)} HTG</div><small>Reste à déposer : {remainderToPay.toFixed(2)}</small></Alert></Col>
              <Col md={3}><Alert variant="light" className="mb-0"><strong>Retraits</strong><div>{sumDebit.toFixed(2)} HTG</div><small>Maximum actuel : {maxWithdrawAllowed.toFixed(2)}</small></Alert></Col>
            </Row>

            <ProtectedSection code={editingTx ? "TRANS_UPDATE" : "TRANS_CREATE"}>
            <Form onSubmit={handleSubmit(onSubmit)} className="mb-4">
              <h6>{editingTx ? 'Modifier la transaction' : 'Nouvelle transaction'}</h6>
              <Row className="g-3 align-items-end">
                <Col md={3}><Form.Group><Form.Label>Type</Form.Label><Form.Select {...register('type')} disabled={isClosed && !editingTx}><option value="CREDIT">Dépôt</option><option value="DEBIT">Retrait</option></Form.Select></Form.Group></Col>
                <Col md={3}><Form.Group><Form.Label>Montant *</Form.Label><Form.Control type="number" step="0.01" {...register('amount', { required: 'Requis', min: { value: 0.01, message: '> 0' } })} />{errors.amount && <Form.Text className="text-danger">{errors.amount.message}</Form.Text>}</Form.Group></Col>
                <Col md={3}><Button type="submit" className="w-100" disabled={!!localRuleError}>{editingTx ? 'Enregistrer' : 'Valider'}</Button></Col>
                <Col md={3}>{editingTx && <Button type="button" variant="outline-secondary" className="w-100" onClick={cancelEdit}>Annuler modification</Button>}</Col>
              </Row>
              <div className="small text-muted mt-2">Total : {totalValue.toFixed(2)} • Valeur finale : {finalValue.toFixed(2)} • Frais : {feePercent.toFixed(2)}% + {feeFixed.toFixed(2)} HTG • Réserve minimum : {minReserve.toFixed(2)} HTG</div>
            </Form>
            </ProtectedSection>

            <div className="d-flex justify-content-between align-items-center mb-2"><h6 className="mb-0">Historique des transactions</h6>{remainingToClient > 0 && sumCredit + 0.000001 >= totalValue && !isClosed && <ProtectedSection code="TRANS_CREATE"><Button variant="success" size="sm" onClick={handlePayout}>Paiement final ({remainingToClient.toFixed(2)} HTG)</Button></ProtectedSection>}</div>
            <Row className="g-2 align-items-end mb-3">
              <Col md={2}><Form.Label>Date début</Form.Label><Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Col>
              <Col md={2}><Form.Label>Date fin</Form.Label><Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Col>
              <Col md={3}><Form.Label>Recherche</Form.Label><InputGroup><Form.Control placeholder="Code, utilisateur..." value={filter} onChange={(e) => setFilter(e.target.value)} /><Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button></InputGroup></Col>
              <Col md={2}><Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">Tous types</option><option value="CREDIT">Dépôts</option><option value="DEBIT">Retraits</option></Form.Select></Col>
              <Col md={2}><Form.Select value={sortKey} onChange={(e) => setSortKey(e.target.value)}><option value="createdAt">Date</option><option value="amount">Montant</option><option value="balanceAfter">Solde</option><option value="type">Type</option></Form.Select></Col>
              <Col md={1}><Form.Select value={sortDir} onChange={(e) => setSortDir(e.target.value)}><option value="desc">↓</option><option value="asc">↑</option></Form.Select></Col>
            </Row>
            <Row className="mb-2"><Col md={3}><Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value={10}>10 / page</option><option value={25}>25 / page</option><option value={50}>50 / page</option><option value={100}>100 / page</option></Form.Select></Col></Row>

            <Table hover responsive><thead><tr><th>Date</th><th>Code</th><th>Type</th><th>Montant</th><th>Solde après</th><th>Utilisateur</th><th>Actions</th></tr></thead><tbody>
              {txs.map((tx) => <tr key={tx.id}><td>{new Date(tx.createdAt).toLocaleString()}</td><td><Badge bg="dark">{tx.codeTransaction || '—'}</Badge></td><td>{tx.type === 'CREDIT' ? 'Dépôt' : 'Retrait'}</td><td>{Number(tx.amount).toFixed(2)}</td><td>{Number(tx.balanceAfter).toFixed(2)}</td><td>{tx.agent?.username || '—'}</td><td>
                <ReceiptPrintButton className="me-2 mb-1" getDocument={() => buildReceiptDocument(tx)} />
                <ProtectedSection code="TRANS_UPDATE"><Button size="sm" variant="outline-secondary" className="me-2 mb-1" onClick={() => handleEdit(tx)}>Modifier</Button></ProtectedSection>
                <ProtectedSection code="TRANS_DELETE"><Button size="sm" variant="outline-danger" className="mb-1" onClick={() => handleDelete(tx.id)}>Supprimer</Button></ProtectedSection>
              </td></tr>)}
              {txs.length === 0 && <tr><td colSpan={7} className="text-center text-muted">Aucune transaction pour cette période.</td></tr>}
            </tbody></Table>
            <div className="d-flex justify-content-between align-items-center"><span className="text-muted">Résultats : {total} • Page {page}/{totalPages}</span><Pagination className="mb-0">{paginationItems}</Pagination></div>
          </>}
        </>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Fermer</Button></Modal.Footer>
    </Modal>
  );
}
